import { ArticleSortType } from '@wix/answers-api';
import { voteUnVoteForWishList } from '@wix/bi-logger-new-help-center/v2';
import { Box, Text, ToggleButton } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { Heart, HeartFilled, ThumbsUp, ThumbsUpFilled } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
} from 'react';

import { BI } from '../../../constants';
import { DATA_HOOKS } from '../../../dataHooks';
import { useBI } from '../../../hooks/useBI';
import { useResponsive } from '../../../hooks/useResponsive';
import { useVoteType } from '../../../hooks/useVoteType';
import {
  ArticleSearchResultItem,
  RoadmapETAorVotedFilter,
  VoteType,
} from '../../../types';
import { loginWithAnswersSSO } from '../../../utils/login';
import {
  FilterToName,
  sortTypeToName,
  tabToName,
} from '../../../utils/roadmap';
import { COLORS } from '../../../utils/theme';
import { useStore } from '../../../zustand/stores';

import css from './index.module.scss';

export const voteTypeToBiVoteType = {
  [VoteType.FOLLOW]: BI.VOTE_TYPE.FEATURE_REQUEST_FOLLOW,
  [VoteType.VOTE]: BI.VOTE_TYPE.FEATURE_REQUEST_VOTE,
};

export type RoadmapVoteProps = {
  article: ArticleSearchResultItem;
  followArticle: (articleId: string) => void;
  unFollowArticle: (articleId: string) => void;
  locale: string;
  filter?: RoadmapETAorVotedFilter;
  sortType?: ArticleSortType;
  isFollowingArticle: boolean;
};
export const LABELS_LENGTH = 3;

export const MAX_PREVIEW_SIZE = 300;

const RoadmapVote: FunctionComponent<RoadmapVoteProps> = ({
  article,
  followArticle,
  unFollowArticle,
  locale,
  filter,
  sortType,
  isFollowingArticle,
}: RoadmapVoteProps) => {
  const { t } = useTranslation();
  const { sendBIEvent } = useBI();
  const [showVoteContent, setShowVoteContent] = React.useState(false);
  const [isFollowing, setIsFollowing] = React.useState(false);
  const { isMobile } = useResponsive();
  const { user } = useStore((state) => state.userSlice);
  const voteType = useVoteType(article);

  const isPreviousVote = useCallback(
    (): boolean => isFollowingArticle,
    [isFollowingArticle]
  );

  useEffect(() => {
    setIsFollowing(isPreviousVote());
  }, [isPreviousVote]);

  const clickFollowArticle = async () => {
    if (user !== null) {
      followArticle(article.id);
      setIsFollowing(true);
      setShowVoteContent(true);
      await sendVoteBiEvent();
    } else {
      loginWithAnswersSSO(location.href, locale);
    }
  };
  const clickUnFollowArticle = async () => {
    unFollowArticle(article.id);
    setIsFollowing(false);
    setShowVoteContent(false);
    await sendVoteBiEvent();
  };

  const sendVoteBiEvent = async () =>
    sendBIEvent(
      voteUnVoteForWishList({
        source_name: BI.SOURCE_NAMES.ROADMAP,
        kb_lang: locale as string,
        item_id: article.id,
        tab: article.resolution ? tabToName(article.resolution) : undefined,
        sort_by: sortType && sortTypeToName(sortType),
        filter_by: filter && FilterToName[filter],
        thumbs_up: !isFollowing,
        vote_type: voteTypeToBiVoteType[voteType],
      })
    );

  const onClick = () => {
    switch (voteType) {
      case VoteType.FOLLOW:
      case VoteType.VOTE:
        if (isFollowing) {
          return clickUnFollowArticle();
        }
        return clickFollowArticle();
    }
  };

  const isToggleButtonSelected = React.useMemo(() => isFollowing, [isFollowing]);
  const voteButtonDisabled = false;
  const { text: buttonText, icon: buttonIcon } = React.useMemo(() => {
    switch (voteType) {
      case VoteType.FOLLOW:
        return isFollowing
          ? { text: t('roadmap.article-item.following'), icon: <HeartFilled /> }
          : {
            text: t('roadmap.article-item.follow'),
            icon: <Heart className={css.followIcon} />,
          };
      case VoteType.VOTE:
        return isFollowing
          ? { text: t('roadmap.article-item.voted'), icon: <ThumbsUpFilled /> }
          : { text: t('roadmap.article-item.vote'), icon: <ThumbsUp /> };
    }
  }, [voteType, isFollowing, t]);
  const buttonLabel = React.useMemo(
    () => (
      <span
        className={classNames(css.followButtonLabel, {
          [css.followButtonLabelDisabled]: voteButtonDisabled,
        })}
      >
        {buttonText}
      </span>
    ),
    [buttonText, voteButtonDisabled]
  );
  const voteContent = React.useMemo(() => {
    if (showVoteContent) {
      if (isMobile) {
        return null;
      }
      return (
        <Text className={css.voteContent} textAlign="center" size="tiny">
          {t('roadmap.article-item.vote-content')}
        </Text>
      );
    }
    return null;
  }, [showVoteContent, t]);

  if (isMobile) {
    return (
      <Box direction="vertical" align="right" flexShrink={1}>
        <ToggleButton
          className={isToggleButtonSelected ? css.mobileVotedButton : undefined}
          onClick={onClick}
          size="small"
          selected={isToggleButtonSelected}
          shape="round"
          border={true}
          labelPlacement="end"
          disabled={voteButtonDisabled}
          dataHook={DATA_HOOKS.FOLLOW_VOTE_ARTICLE_BTN}
        >
          {buttonIcon}
        </ToggleButton>
        {voteContent}
      </Box>
    );
  }

  return (
    <Box
      minHeight={72}
      height={'100%'}
      direction="vertical"
      className={css.followArticle}
      align="center"
    >
      <ToggleButton
        shape="pill"
        color={COLORS.B50}
        border={true}
        dataHook={DATA_HOOKS.FOLLOW_VOTE_ARTICLE_BTN}
        labelValue={buttonLabel}
        selected={isToggleButtonSelected}
        labelPlacement="end"
        disabled={voteButtonDisabled}
        onClick={onClick}
      >
        {buttonIcon}
      </ToggleButton>
      {voteContent}
    </Box>
  );
};

export default RoadmapVote;
